import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { CaretDown, MagnifyingGlass, Check } from '@phosphor-icons/react'
import { cn } from '@/lib/utils'

interface Country {
  name: string
  iso: string
  dial: string
}

// Common countries (name / ISO-3166 alpha-2 / dial code). Flags are derived
// from the ISO code via regional-indicator code points — no image assets.
const COUNTRIES: Country[] = [
  { name: 'United States', iso: 'US', dial: '+1' },
  { name: 'Canada', iso: 'CA', dial: '+1' },
  { name: 'United Kingdom', iso: 'GB', dial: '+44' },
  { name: 'India', iso: 'IN', dial: '+91' },
  { name: 'Australia', iso: 'AU', dial: '+61' },
  { name: 'Germany', iso: 'DE', dial: '+49' },
  { name: 'France', iso: 'FR', dial: '+33' },
  { name: 'Spain', iso: 'ES', dial: '+34' },
  { name: 'Italy', iso: 'IT', dial: '+39' },
  { name: 'Netherlands', iso: 'NL', dial: '+31' },
  { name: 'Belgium', iso: 'BE', dial: '+32' },
  { name: 'Switzerland', iso: 'CH', dial: '+41' },
  { name: 'Austria', iso: 'AT', dial: '+43' },
  { name: 'Sweden', iso: 'SE', dial: '+46' },
  { name: 'Norway', iso: 'NO', dial: '+47' },
  { name: 'Denmark', iso: 'DK', dial: '+45' },
  { name: 'Finland', iso: 'FI', dial: '+358' },
  { name: 'Ireland', iso: 'IE', dial: '+353' },
  { name: 'Portugal', iso: 'PT', dial: '+351' },
  { name: 'Poland', iso: 'PL', dial: '+48' },
  { name: 'Czechia', iso: 'CZ', dial: '+420' },
  { name: 'Greece', iso: 'GR', dial: '+30' },
  { name: 'Romania', iso: 'RO', dial: '+40' },
  { name: 'Hungary', iso: 'HU', dial: '+36' },
  { name: 'Ukraine', iso: 'UA', dial: '+380' },
  { name: 'Russia', iso: 'RU', dial: '+7' },
  { name: 'Turkey', iso: 'TR', dial: '+90' },
  { name: 'Israel', iso: 'IL', dial: '+972' },
  { name: 'United Arab Emirates', iso: 'AE', dial: '+971' },
  { name: 'Saudi Arabia', iso: 'SA', dial: '+966' },
  { name: 'Qatar', iso: 'QA', dial: '+974' },
  { name: 'South Africa', iso: 'ZA', dial: '+27' },
  { name: 'Nigeria', iso: 'NG', dial: '+234' },
  { name: 'Kenya', iso: 'KE', dial: '+254' },
  { name: 'Egypt', iso: 'EG', dial: '+20' },
  { name: 'Brazil', iso: 'BR', dial: '+55' },
  { name: 'Mexico', iso: 'MX', dial: '+52' },
  { name: 'Argentina', iso: 'AR', dial: '+54' },
  { name: 'Chile', iso: 'CL', dial: '+56' },
  { name: 'Colombia', iso: 'CO', dial: '+57' },
  { name: 'China', iso: 'CN', dial: '+86' },
  { name: 'Japan', iso: 'JP', dial: '+81' },
  { name: 'South Korea', iso: 'KR', dial: '+82' },
  { name: 'Singapore', iso: 'SG', dial: '+65' },
  { name: 'Hong Kong', iso: 'HK', dial: '+852' },
  { name: 'Malaysia', iso: 'MY', dial: '+60' },
  { name: 'Indonesia', iso: 'ID', dial: '+62' },
  { name: 'Philippines', iso: 'PH', dial: '+63' },
  { name: 'Thailand', iso: 'TH', dial: '+66' },
  { name: 'Vietnam', iso: 'VN', dial: '+84' },
  { name: 'Pakistan', iso: 'PK', dial: '+92' },
  { name: 'Bangladesh', iso: 'BD', dial: '+880' },
  { name: 'New Zealand', iso: 'NZ', dial: '+64' },
]

/** Group a digit string into readable, space-separated blocks (3 · 3 · rest). */
function formatNational(digits: string): string {
  const d = digits.replace(/\D/g, '')
  if (d.length <= 3) return d
  if (d.length <= 6) return `${d.slice(0, 3)} ${d.slice(3)}`
  return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6)}`
}

/** Number of digits in a string. The caret's offset expressed in digits is the
 *  only position index that survives re-formatting (§ caret preservation). */
function countDigits(s: string): number {
  return (s.match(/\d/g) || []).length
}

/** Index in `formatted` immediately after its `n`-th digit (`n === 0` → 0). */
function caretIndexAfterDigits(formatted: string, n: number): number {
  if (n <= 0) return 0
  let seen = 0
  for (let i = 0; i < formatted.length; i += 1) {
    if (/\d/.test(formatted[i])) {
      seen += 1
      if (seen === n) return i + 1
    }
  }
  return formatted.length
}

function flagEmoji(iso: string): string {
  return iso
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)))
}

/**
 * Dial codes matched longest-first so "+1" cannot shadow a longer "+1…"-style
 * code.
 *
 * AMBIGUITY, DELIBERATELY LEFT IN PLACE: several countries share a dial code —
 * in this list `US` and `CA` are both `+1` — and `Array.prototype.sort` is
 * stable, so a tie always resolves to whichever appears first in `COUNTRIES`
 * (United States). A stored `"+1 …"` string therefore *cannot* tell us whether
 * the user meant US or CA; no comparator can fix that, because the information
 * is not in the string.
 *
 * That is why the component treats its `iso` state as the authority and only
 * re-derives it from `value` when `value` carries a *different* dial code (see
 * the sync effect). Do NOT "simplify" that back to
 * `setIso(parsePhone(value).iso)` — it makes Canada unpickable.
 */
const COUNTRIES_BY_DIAL_DESC = [...COUNTRIES].sort((a, b) => b.dial.length - a.dial.length)

const DEFAULT_ISO = 'US'

/**
 * Best-effort split of a stored "+<dial> <number>" string into country + number.
 *
 * `iso` is `null` when the value carries no recognisable dial code (including
 * the empty string). Callers MUST keep whatever ISO they already hold in that
 * case — the old `iso: 'US'` fallback is exactly what silently wiped a chosen
 * country every time the number field was cleared.
 */
function parsePhone(value: string): { iso: string | null; number: string } {
  const trimmed = (value || '').trim()
  if (!trimmed) return { iso: null, number: '' }
  const match = COUNTRIES_BY_DIAL_DESC.find((c) => trimmed.startsWith(c.dial))
  // `number` is always stored as bare digits — formatting is presentational.
  if (match) return { iso: match.iso, number: trimmed.slice(match.dial.length).replace(/\D/g, '') }
  return { iso: null, number: trimmed.replace(/\D/g, '') }
}

function dialFor(iso: string): string | undefined {
  return COUNTRIES.find((c) => c.iso === iso)?.dial
}

interface PhoneInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  /** Accessible name for the number field. The call site's visible `<label>`
   *  has no `htmlFor` to bind to, so the field names itself (§56.1). */
  ariaLabel?: string
}

/** Portalled menu geometry — width matches the old `w-72`, height the search
 *  block (48px) + the `max-h-60` list (240px) + its 8px padding. */
const MENU_WIDTH = 288
const MENU_MAX_HEIGHT = 296

export const PhoneInput: React.FC<PhoneInputProps> = ({ value, onChange, placeholder, ariaLabel }) => {
  // The selected ISO is COMPONENT STATE, seeded once from `value`. It is never
  // derived on the fly, because `parsePhone` cannot recover a country from an
  // empty string or from a dial code two countries share.
  const [iso, setIso] = useState(() => parsePhone(value).iso ?? DEFAULT_ISO)
  const [number, setNumber] = useState(() => parsePhone(value).number)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIdx, setActiveIdx] = useState(0)
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null)

  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLUListElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  // Stable id base so the trigger can point `aria-activedescendant` at the
  // highlighted option — that is what makes arrow-key nav announce (§56).
  // Mirrors `Select.tsx`.
  const uid = React.useId()
  const listboxId = `${uid}listbox`
  const optionId = useCallback((index: number) => `${uid}opt-${index}`, [uid])

  const country = COUNTRIES.find((c) => c.iso === iso) || COUNTRIES[0]

  // Sync from the parent ONLY where the incoming value actually carries new
  // information. Two guards, both load-bearing:
  //
  //  1. `parsed.iso === null` (empty value, or a value with no dial code) must
  //     NOT touch the ISO. The old code called `setIso(parsePhone('').iso)`,
  //     whose 'US' fallback silently reverted the user's country the instant
  //     they cleared the number field.
  //  2. A parsed ISO whose dial code EQUALS the current selection's is treated
  //     as a round-trip of our own `emit`, not an external change. Without this
  //     an explicit Canada pick would be dragged back to United States on the
  //     very next render, because "+1 …" parses to US (see
  //     COUNTRIES_BY_DIAL_DESC).
  useEffect(() => {
    const parsed = parsePhone(value)
    setNumber((prev) => (parsed.number === prev ? prev : parsed.number))
    if (parsed.iso && parsed.iso !== iso && dialFor(parsed.iso) !== dialFor(iso)) {
      setIso(parsed.iso)
    }
     
  }, [value])

  // What we publish upward for "country chosen, no digits yet": the EMPTY
  // STRING. The dial code alone ("+44") is not a phone number, and the parent
  // persists this value verbatim — emitting "+44" would store junk and make
  // required-field checks pass on an empty input. The chosen country still
  // survives, because it lives in `iso` state and the effect above refuses to
  // clobber it from an empty value. Trade-off: a country picked but never
  // followed by digits is not persisted across a remount. That is intentional —
  // no digits means no phone number.
  const emit = (nextIso: string, nextDigits: string) => {
    const c = COUNTRIES.find((x) => x.iso === nextIso) || COUNTRIES[0]
    const d = nextDigits.replace(/\D/g, '')
    onChange(d ? `${c.dial} ${formatNational(d)}` : '')
  }

  const close = useCallback(() => {
    setOpen(false)
    setQuery('')
    // Focus lives in the portalled search field while the menu is open; once
    // that node unmounts focus would fall to <body> (a focus black hole, §56.4).
    // Hand it back to the trigger the user opened.
    if (menuRef.current?.contains(document.activeElement)) triggerRef.current?.focus()
  }, [])

  const openMenu = useCallback(() => {
    // Seed the highlight on the current selection so `aria-activedescendant`
    // announces something meaningful the moment the listbox appears. `activeIdx`
    // indexes `filtered`, and `query` is always '' while closed (see `close`),
    // so on the commit that opens, `filtered === COUNTRIES` and this index is
    // valid in both.
    setActiveIdx(Math.max(0, COUNTRIES.findIndex((c) => c.iso === iso)))
    setOpen(true)
  }, [iso])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return COUNTRIES
    return COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dial.replace('+', '').includes(q.replace('+', '')),
    )
  }, [query])

  // Fixed-position coords for the portalled menu, clamped to the viewport so a
  // 288px menu never runs off the right edge or below the fold (§19). Mirrors
  // `Select.tsx`'s `computeMenuStyle`.
  const computeMenuStyle = useCallback((): React.CSSProperties | null => {
    const rect = containerRef.current?.getBoundingClientRect()
    if (!rect) return null
    const margin = 6
    const viewportH = window.innerHeight
    const viewportW = window.innerWidth
    const width = Math.min(MENU_WIDTH, viewportW - margin * 2)
    const spaceBelow = viewportH - rect.bottom - margin
    const spaceAbove = rect.top - margin
    // Open upward only when below is cramped AND above has more room.
    const openUp = spaceBelow < Math.min(MENU_MAX_HEIGHT, 200) && spaceAbove > spaceBelow
    const available = Math.max(0, openUp ? spaceAbove : spaceBelow)
    const maxHeight = Math.min(MENU_MAX_HEIGHT, available)
    const left = Math.max(margin, Math.min(rect.left, viewportW - width - margin))
    const base: React.CSSProperties = { position: 'fixed', left, width, maxHeight, zIndex: 1000 }
    // Anchor the bottom edge when opening up so a short menu hugs the control.
    if (openUp) base.bottom = viewportH - rect.top + margin
    else base.top = rect.bottom + margin
    return base
  }, [])

  // Position once on open, then DISMISS on scroll + resize (§54). The menuRef
  // guard is required: the country list is a long `overflow-y-auto` scroller and
  // `scroll` does not bubble, so the capture-phase listener also sees its own
  // wheel ticks and would close the menu on the first one.
  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null)
      return
    }
    setMenuStyle(computeMenuStyle())
    const onScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return
      close()
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', close)
    }
  }, [open, computeMenuStyle, close])

  // Outside click — the menu is portalled to <body>, so it is no longer a DOM
  // descendant of the container and must be checked separately.
  useEffect(() => {
    if (!open) return
    const onClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (containerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      close()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        close()
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    window.addEventListener('keydown', onKeyDown)
    // Autofocus the search so the user can immediately type a country name/code.
    const focusTimer = window.setTimeout(() => searchRef.current?.focus(), 0)
    return () => {
      document.removeEventListener('mousedown', onClickOutside)
      window.removeEventListener('keydown', onKeyDown)
      window.clearTimeout(focusTimer)
    }
  }, [open, close])

  useEffect(() => {
    setActiveIdx(0)
  }, [query])

  const selectCountry = (c: Country) => {
    // The explicit pick is authoritative and outranks any prefix inference the
    // sync effect could make from `value` — the only way US/CA (+1) and any
    // future dial-code twins are reachable at all.
    setIso(c.iso)
    close()
    emit(c.iso, number)
  }

  // ── Caret preservation ────────────────────────────────────────────────────
  // The input renders space-grouped digits (`formatNational`) while state holds
  // bare digits, so every keystroke re-formats the whole string. Re-rendering a
  // controlled input with a different value collapses the selection to the end,
  // which threw the caret to the end of the field on any mid-string edit.
  //
  // The fix: express the caret in DIGITS BEFORE IT — the one index that survives
  // re-formatting — then translate it back to a string offset once the new
  // formatted value is in the DOM.

  /** Digit offset the caret must land after on the next commit. */
  const pendingCaretRef = useRef<number | null>(null)

  const applyPendingCaret = useCallback(() => {
    const digits = pendingCaretRef.current
    if (digits == null) return
    pendingCaretRef.current = null
    const el = inputRef.current
    if (!el || document.activeElement !== el) return
    const pos = caretIndexAfterDigits(el.value, digits)
    el.setSelectionRange(pos, pos)
  }, [])

  // Runs after every commit and no-ops unless a keystroke queued a caret
  // target. A layout effect, not a plain one: it must land before the browser
  // paints, or the caret visibly jumps and back.
  useLayoutEffect(applyPendingCaret)

  /**
   * Single funnel for every number edit. `raw` is the would-be raw field value
   * (formatting characters and all) and `caret` its caret offset within it.
   */
  const commitNumber = (raw: string, caret: number) => {
    pendingCaretRef.current = countDigits(raw.slice(0, caret))
    const digits = raw.replace(/\D/g, '')
    if (digits === number) {
      // React bails out when state is unchanged (the user typed a character we
      // reject, or deleted a formatting space), so no commit happens and the
      // layout effect never runs. Push the re-formatted value and the caret to
      // the DOM node by hand — otherwise the rejected character would sit in
      // the field with React none the wiser.
      const el = inputRef.current
      if (el) el.value = formatNational(digits)
      applyPendingCaret()
      return
    }
    setNumber(digits)
    emit(iso, digits)
  }

  /**
   * Backspace / Delete immediately next to a formatting space used to be a
   * silent no-op: the browser removed the space, `formatNational` put it
   * straight back, and the digit count never changed. Reach past the separator
   * and delete the neighbouring DIGIT instead.
   */
  const onNumberKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Backspace' && e.key !== 'Delete') return
    const el = e.currentTarget
    const start = el.selectionStart ?? 0
    const end = el.selectionEnd ?? 0
    if (start !== end) return // range deletes are unambiguous — let them through
    const text = el.value
    const back = e.key === 'Backspace'
    // Index of the character the browser would remove.
    const at = back ? start - 1 : start
    if (at < 0 || at >= text.length) return
    if (/\d/.test(text[at])) return // deleting a digit already works
    e.preventDefault()
    // Walk over the separator(s) to the nearest digit in the same direction.
    let i = at
    while (i >= 0 && i < text.length && !/\d/.test(text[i])) i += back ? -1 : 1
    if (i < 0 || i >= text.length) return
    commitNumber(text.slice(0, i) + text.slice(i + 1), i)
  }

  const onSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (filtered[activeIdx]) selectCountry(filtered[activeIdx])
    } else if (e.key === 'Escape') {
      close()
    }
  }

  // Keep the active option inside the menu's own scroller, so the highlight
  // that `aria-activedescendant` points at is never somewhere unseen.
  //
  // `menuStyle` is a dependency, not just a guard: the `<ul>` only mounts on the
  // commit that sets it, so without it the seeded option (a country far down the
  // list) would never be scrolled to on open. Safe against §54 — the scroll
  // dismissal handler early-returns for targets inside `menuRef`.
  useEffect(() => {
    if (!open || !menuStyle) return
    const el = document.getElementById(optionId(activeIdx))
    if (el && listRef.current?.contains(el)) el.scrollIntoView({ block: 'nearest' })
  }, [open, menuStyle, activeIdx, optionId])

  const activeOption = open ? filtered[activeIdx] : undefined

  return (
    <div ref={containerRef} className="relative">
      <div
        className={cn(
          'flex items-stretch h-9 rounded-md overflow-hidden transition-colors duration-150',
          'bg-background dark:bg-white/[0.02] border border-border/60 dark:border-white/[0.08]',
          'hover:border-border/80 dark:hover:border-white/[0.14]',
          'focus-within:border-[#7170ff]/55 dark:focus-within:border-[#7170ff]/75',
          // Focus moves into the PORTALLED search field while the menu is open,
          // so `focus-within` no longer matches this wrapper and the control
          // would show no active state at all. Same fix as `Select.tsx`.
          open && 'border-[#7170ff]/55 dark:border-[#7170ff]/75',
        )}
      >
        {/* Country trigger — a select-only combobox collapsed to its value (§56) */}
        <button
          ref={triggerRef}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={activeOption ? optionId(activeIdx) : undefined}
          aria-label={`Country calling code: ${country.name} ${country.dial}`}
          onClick={() => {
            if (open) close()
            else openMenu()
          }}
          // A closed trigger opens on ArrowDown / ArrowUp / Enter / Space. Bail
          // while open so this never competes with `onSearchKeyDown`.
          onKeyDown={(e) => {
            if (open) return
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              openMenu()
            }
          }}
          // The wrapper's border already signals "this control has focus", but it
          // cannot say *which* half. This segment owns no border of its own
          // (that would break the seam), so keyboard focus reads as a surface
          // tint instead of a ring (§13's intent, applied to nested chrome).
          className="flex items-center gap-1.5 pl-3 pr-2.5 text-foreground dark:text-[#f7f8f8] hover:bg-muted/40 dark:hover:bg-white/[0.03] focus:outline-none focus-visible:bg-muted/60 dark:focus-visible:bg-white/[0.06] transition-colors duration-150 border-r border-border/60 dark:border-white/[0.08]"
        >
          <span className="text-base leading-none" aria-hidden="true">{flagEmoji(country.iso)}</span>
          <span aria-hidden="true" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>{country.dial}</span>
          <CaretDown className={cn('w-3 h-3 text-muted-foreground transition-transform duration-150', open && 'rotate-180')} weight="bold" aria-hidden="true" />
        </button>

        {/* Number input */}
        <input
          ref={inputRef}
          type="tel"
          inputMode="numeric"
          autoComplete="tel-national"
          // The call site's visible `<label>` carries no `htmlFor`, and this
          // file cannot add one, so the field names itself (§56.1).
          aria-label={ariaLabel || 'Phone number'}
          value={formatNational(number)}
          onChange={(e) => {
            // Everything (including rejecting non-digits) goes through
            // `commitNumber`, which is also what keeps the caret put.
            const el = e.currentTarget
            commitNumber(el.value, el.selectionStart ?? el.value.length)
          }}
          onKeyDown={onNumberKeyDown}
          placeholder={placeholder || '123 456 7890'}
          className="flex-1 min-w-0 px-3 bg-transparent text-foreground dark:text-[#f7f8f8] placeholder:text-muted-foreground/50 dark:placeholder:text-[#62666d] outline-none"
          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
        />
      </div>

      {createPortal(
        <AnimatePresence>
          {open && menuStyle && (
            <motion.div
              ref={menuRef}
              initial={{ opacity: 0, scale: 0.98, y: menuStyle.bottom != null ? 4 : -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: menuStyle.bottom != null ? 4 : -4 }}
              transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
              style={menuStyle}
              className={cn(
                'flex flex-col rounded-lg border bg-card border-border/60 shadow-lg dark:bg-[#191a1b] dark:border-white/[0.08] overflow-hidden',
                menuStyle.bottom != null ? 'origin-bottom' : 'origin-top',
              )}
            >
              {/* Search — nested chrome inside a control, stays h-8 per §53 */}
              <div className="flex-shrink-0 p-2 border-b border-border/50 dark:border-white/[0.06]">
                <div className="relative">
                  <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground dark:text-[#62666d]" aria-hidden="true" />
                  <input
                    ref={searchRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onSearchKeyDown}
                    // The trigger owns `role="combobox"` (it is the collapsed
                    // control), but FOCUS lives here while the menu is open, and
                    // `aria-activedescendant` is only announced on the focused
                    // node — so the filter field repeats it. `textbox` supports
                    // both of these attributes. Without this, arrow-key nav is
                    // silent no matter what the trigger says.
                    aria-label="Search country or dial code"
                    aria-autocomplete="list"
                    aria-controls={listboxId}
                    aria-activedescendant={activeOption ? optionId(activeIdx) : undefined}
                    placeholder="Search country or code…"
                    className="w-full h-8 pl-8 pr-3 rounded-md bg-background dark:bg-white/[0.02] border border-border/60 dark:border-white/[0.08] text-foreground dark:text-[#f7f8f8] placeholder:text-muted-foreground/50 dark:placeholder:text-[#62666d] focus:outline-none focus-visible:border-[#7170ff]/55 dark:focus-visible:border-[#7170ff]/75 transition-colors"
                    style={{ fontSize: '13px' }}
                  />
                </div>
              </div>

              {/* List — `min-h-0` lets it shrink (and scroll) when the panel is
                  clamped to a short viewport instead of being clipped.

                  One node, one role (§57): each `<li role="option">` IS the
                  interactive element — it carries the handlers and the
                  hover/active styling, and its children are inert `<span>`s.
                  A focusable <button> inside an option is invalid ARIA, and it
                  also used to be why selection needed a `preventDefault`ed
                  mousedown to stop the button stealing focus from the search
                  field. `<li>` is not focusable, so a plain `onClick` is enough.
                  Keyboard nav lives in `onSearchKeyDown` and is surfaced via
                  `aria-activedescendant`. */}
              <ul
                ref={listRef}
                id={listboxId}
                role="listbox"
                tabIndex={-1}
                aria-label="Countries"
                className="min-h-0 max-h-60 overflow-y-auto py-1"
              >
                {filtered.length === 0 ? (
                  <li role="presentation" className="px-3 py-4 text-center text-[12px] text-muted-foreground dark:text-[#8a8f98]">No matches</li>
                ) : (
                  filtered.map((c, i) => {
                    const isSel = c.iso === iso
                    return (
                      <li
                        key={`${c.iso}-${c.dial}`}
                        id={optionId(i)}
                        role="option"
                        aria-selected={isSel}
                        onMouseEnter={() => setActiveIdx(i)}
                        onClick={() => selectCountry(c)}
                        className={cn(
                          'w-full flex items-center gap-2.5 px-3 py-1.5 text-left cursor-pointer transition-colors duration-100',
                          i === activeIdx ? 'bg-[#5e6ad2]/[0.08] dark:bg-[#5e6ad2]/[0.12]' : 'hover:bg-muted/40 dark:hover:bg-white/[0.03]',
                        )}
                      >
                        <span className="text-base leading-none" aria-hidden="true">{flagEmoji(c.iso)}</span>
                        <span className="flex-1 truncate text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>{c.name}</span>
                        <span className="text-muted-foreground dark:text-[#8a8f98] tabular-nums" style={{ fontSize: '12px' }}>{c.dial}</span>
                        {isSel && <Check className="w-3.5 h-3.5 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="bold" aria-hidden="true" />}
                      </li>
                    )
                  })
                )}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  )
}

export default PhoneInput
